import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Form, Input, message } from 'antd';
import { CustomButton } from '@/components';
import CustomTitle from '@/components/CustomTitle';
import CustomModalWithoutButtons from '@/components/CustomModalWithoutButtons';
import {
  StarIcon,
  InboundIcon,
  OutboundIcon,
  MailIcon,
  CallIcon,
} from '@/assets/icons/icons';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { GetSettingsProfile } from '@/apis/settings';
import { useToken } from '@/common/helper';
import { LoadingIcon } from './LoadingIcon';

type Channel = 'call' | 'email';

type SetupAgentTestModalProps = {
  open: boolean;
  setOpen: (v: boolean) => void;
  onSubmit?: (payload: {
    channel: Channel;
    phone?: string;
    email?: string;
  }) => void;
  onClose?: () => void;
  defaultChannel?: Channel;
  loading?: boolean;
  callEnabled?: boolean;
  emailEnabled?: boolean;
  testResultsPolling?: boolean;
  testResults?: {
    status: string;
    issue_resolved: boolean;
    pca_results: Record<string, unknown> | null;
    transcript: string;
    summary: string;
    call_outcome: string;
    user_sentiment: string;
  } | null;
};

export const SetupAgentTestModal: React.FC<SetupAgentTestModalProps> = ({
  open,
  setOpen,
  onSubmit,
  onClose,
  defaultChannel = 'call',
  loading = false,
  callEnabled = true,
  emailEnabled = true,
  testResultsPolling = false,
  testResults = null,
}) => {
  const handleClose = useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [setOpen, onClose]);
  const [form] = Form.useForm();
  const [channel, setChannel] = useState<Channel>(defaultChannel);
  const { token } = useToken();
  const [profileData, setProfileData] = useState<{
    phone?: string;
    email?: string;
  }>({});

  const validateNow = useCallback(async () => {
    try {
      const fieldToValidate = channel === 'call' ? 'phone' : 'email';
      await form.validateFields([fieldToValidate]);
      return true;
    } catch {
      return false;
    }
  }, [form, channel]);

  useEffect(() => {
    if (!open) return;

    const initialChannel =
      callEnabled && defaultChannel === 'call'
        ? 'call'
        : emailEnabled && defaultChannel === 'email'
          ? 'email'
          : callEnabled
            ? 'call'
            : 'email';

    setChannel(initialChannel);
    form.resetFields();
    setProfileData({});

    if (token) {
      GetSettingsProfile(token)
        .then(async (res) => {
          const data = res?.data?.data?.data;
          const storedProfile = {
            phone: data?.phone_number || undefined,
            email: data?.email || undefined,
          };
          setProfileData(storedProfile);

          if (storedProfile.phone) {
            form.setFieldsValue({ phone: storedProfile.phone });
          }
          if (storedProfile.email) {
            form.setFieldsValue({ email: storedProfile.email });
          }
        })
        .catch(() => {
          message.error('Failed to load profile data');
        });
    }
  }, [open, defaultChannel, form, token, callEnabled, emailEnabled]);

  const handleChannel = useCallback(
    (next: Channel) => {
      setChannel(next);
      if (next === 'call') {
        form.setFieldsValue({
          email: undefined,
          phone: profileData.phone || undefined,
        });
      }
      if (next === 'email') {
        form.setFieldsValue({
          phone: undefined,
          email: profileData.email || undefined,
        });
      }
    },
    [form, profileData]
  );

  const handleSubmit = useCallback(async () => {
    const ok = await validateNow();
    if (!ok) return;

    const values = form.getFieldsValue(true) as {
      phone?: string;
      email?: string;
    };

    onSubmit?.({
      channel,
      phone: values.phone,
      email: values.email,
    });
  }, [channel, form, onSubmit, validateNow]);

  return (
    <CustomModalWithoutButtons
      modalOpen={open}
      setModalOpen={handleClose}
      modalTitle={testResults ? 'Test agent summary' : 'Setup agent test'}
      modalSize={600}
      modalBorderRadius='12px'
      headerExtra={<span />}
      fontSize='28px'
      lineHeight='36px'
      closeIconSize='18px'
    >
      <div className='agentTestModal'>
        {(loading || testResultsPolling) && !testResults ? (
          <div className='agentTestModal__progress'>
            <CustomTitle
              text={
                channel === 'call'
                  ? 'Agent call in progress...'
                  : 'Agent email thread in progress...'
              }
              color='var(--text-primary)'
              fontSize='24px'
              lineHeight='32px'
              fontWeight='600'
              marginBottom={0}
            />
            <LoadingIcon />
          </div>
        ) : (
          <>
            {!testResults && (
              <>
                <div className='agentTestModal__label'>Channel</div>

                <div className='agentTestModal__pillRow'>
                  <button
                    type='button'
                    className={`agentTestModal__pill ${channel === 'call' ? 'active' : ''} ${!callEnabled ? 'agentTestModal__pill--unavailable' : ''}`}
                    onClick={() => handleChannel('call')}
                    disabled={!callEnabled || loading || testResultsPolling}
                  >
                    <span className='agentTestModal__pillIcon'>
                      <CallIcon
                        color={
                          channel === 'call'
                            ? 'var(--text-primary)'
                            : 'var(--text-secondary)'
                        }
                      />
                    </span>
                    <span>Call</span>
                  </button>

                  <button
                    type='button'
                    className={`agentTestModal__pill ${channel === 'email' ? 'active' : ''} ${!emailEnabled ? 'agentTestModal__pill--unavailable' : ''}`}
                    onClick={() => handleChannel('email')}
                    disabled={!emailEnabled || loading || testResultsPolling}
                  >
                    <span className='agentTestModal__pillIcon'>
                      <MailIcon
                        color={
                          channel === 'email'
                            ? 'var(--text-primary)'
                            : 'var(--text-secondary)'
                        }
                      />
                    </span>
                    <span>Email</span>
                  </button>
                </div>

                <div className='agentTestModal__divider' />

                <Form
                  form={form}
                  layout='vertical'
                  initialValues={{ phone: '', email: '' }}
                >
                  {channel === 'call' ? (
                    <>
                      <div className='agentTestModal__fieldHead'>
                        Agent will call you at:
                      </div>

                      <Form.Item
                        name='phone'
                        className='agentTestModal__phoneItem'
                        rules={[
                          {
                            validator: (_, value) => {
                              if (!value || String(value).trim() === '') {
                                return Promise.reject(
                                  new Error('Enter phone number')
                                );
                              }
                              try {
                                if (isValidPhoneNumber(value))
                                  return Promise.resolve();
                                return Promise.reject(
                                  new Error('Enter a valid phone')
                                );
                              } catch {
                                return Promise.reject(
                                  new Error('Enter a valid phone')
                                );
                              }
                            },
                          },
                        ]}
                      >
                        <PhoneInput
                          country='us'
                          value={form.getFieldValue('phone')}
                          onChange={(_, __, ___, formattedValue) => {
                            form.setFieldsValue({ phone: formattedValue });
                          }}
                          inputClass='agentTestModal__phoneInput'
                          buttonClass='agentTestModal__phoneBtn'
                          containerClass='agentTestModal__phoneContainer'
                          dropdownClass='agentTestModal__phoneDropdown'
                        />
                      </Form.Item>
                    </>
                  ) : (
                    <>
                      <div className='agentTestModal__fieldHead'>
                        Agent will email you at:
                      </div>

                      <Form.Item
                        name='email'
                        className='agentTestModal__inputItem'
                        rules={[
                          { required: true, message: 'Enter email' },
                          { type: 'email', message: 'Enter a valid email' },
                        ]}
                      >
                        <Input
                          placeholder='doug@company.com'
                          className='agentTestModal__input'
                        />
                      </Form.Item>
                    </>
                  )}
                </Form>
              </>
            )}
          </>
        )}

        {testResults && testResults.status === 'COMPLETED' && (
          <>
            <div className='agentTestModal__resultBlock'>
              <div className='agentTestModal__resultTitle'>
                Information collected
              </div>

              {testResults.pca_results &&
              Object.keys(testResults.pca_results).length > 0 ? (
                <div className='agentTestModal__resultList'>
                  {Object.entries(testResults.pca_results).map(
                    ([key, value]) => {
                      const hasValue =
                        value !== null &&
                        value !== undefined &&
                        String(value).trim() !== '';

                      const displayValue =
                        typeof value === 'boolean'
                          ? value
                            ? 'Yes'
                            : 'No'
                          : String(value || '');

                      return (
                        <div key={key} className='agentTestModal__resultRow'>
                          <span className='agentTestModal__resultMark'>
                            {hasValue ? '✓' : '✗'}
                          </span>
                          <div className='agentTestModal__resultText'>
                            <span
                              className={`agentTestModal__resultKey ${hasValue ? '' : 'agentTestModal__resultKey--empty'}`}
                            >
                              {key}
                            </span>
                            {hasValue && (
                              <span className='agentTestModal__resultValue'>
                                {displayValue}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              ) : (
                <div className='agentTestModal__resultEmpty'>
                  No information collected
                </div>
              )}
            </div>

            <div className='agentTestModal__resultBlock'>
              <div className='agentTestModal__resultTitle'>
                Success criteria
              </div>

              <div className='agentTestModal__criteria'>
                <span className='agentTestModal__criteriaMark'>✓</span>
                <span className='agentTestModal__criteriaText'>
                  {testResults.summary || 'Test completed successfully'}
                </span>
              </div>
            </div>

            <div className='agentTestModal__resultActions'>
              <CustomButton
                text='Close'
                onClick={handleClose}
                height='44px'
                padding='0 32px'
                borderRadius='10px'
                bgColor='var(--text-primary)'
                color='var(--surface-container)'
                fontSize='16px'
                fontWeight='600'
                boxShadow='none'
              />
            </div>
          </>
        )}

        {!loading && !testResultsPolling && !testResults && (
          <div className='agentTestModal__footer'>
            <CustomButton
              text='Cancel'
              bgColor='var(--surface-container)'
              color='var(--text-primary)'
              boxShadow='none'
              fontSize='16px'
              fontWeight='600'
              height='44px'
              padding='0 18px'
              borderRadius='10px'
              onClick={handleClose}
              className='agentTestModal__cancelBtn'
            />

            <CustomButton
              text={channel === 'call' ? 'Call me now' : 'Email me now'}
              onClick={handleSubmit}
              height='44px'
              padding='0 18px'
              borderRadius='10px'
              bgColor='var(--text-primary)'
              color='var(--surface-container)'
              fontSize='16px'
              fontWeight='600'
              boxShadow='none'
              width='180px'
              iconPosition='left'
              icon={<StarIcon color='var(--surface-container)' />}
              gap='8px'
              isFormValid={loading}
              className='agentTestModal__actionBtn'
            />
          </div>
        )}
      </div>
    </CustomModalWithoutButtons>
  );
};

interface AgentHeaderProps {
  agentType?: string | null;
  isEditing: boolean;
  readOnly?: boolean;
  onTest?: () => void | boolean | Promise<void | boolean>;
  onRunTest?: (payload: {
    channel: Channel;
    phone?: string;
    email?: string;
  }) => void;
  onClearTestResults?: () => void;
  testLoading?: boolean;
  createType?: 'inbound' | 'outbound' | null;
  callEnabled?: boolean;
  emailEnabled?: boolean;
  testResultsPolling?: boolean;
  testResults?: {
    status: string;
    issue_resolved: boolean;
    pca_results: Record<string, unknown> | null;
    transcript: string;
    summary: string;
    call_outcome: string;
    user_sentiment: string;
  } | null;
}

export const AgentHeader: React.FC<AgentHeaderProps> = ({
  agentType,
  isEditing,
  readOnly = false,
  onTest,
  onRunTest,
  onClearTestResults,
  testLoading,
  createType = null,
  callEnabled = true,
  emailEnabled = true,
  testResultsPolling = false,
  testResults = null,
}) => {
  const [testOpen, setTestOpen] = useState(false);

  const handleTestClick = useCallback(async () => {
    const result = await onTest?.();
    if (result === false) return;
    setTestOpen(true);
  }, [onTest]);

  const pill = useMemo(() => {
    const raw = isEditing
      ? String(agentType || '').toLowerCase()
      : String(createType || '').toLowerCase();

    const isInbound = raw === 'inbound';
    const isOutbound = raw === 'outbound';

    if (!isInbound && !isOutbound) return null;

    return (
      <div className='agentTypePill'>
        {isInbound ? (
          <InboundIcon color='var(--text-secondary)' />
        ) : (
          <OutboundIcon color='var(--text-secondary)' />
        )}
        <span className='agentTypePill__text'>
          {isInbound ? 'Inbound' : 'Outbound'}
        </span>
      </div>
    );
  }, [createType, agentType, isEditing]);

  return (
    <>
      <div className='agentFormHeader agentFormHeader--withPill'>
        {pill}

        <div className='agentFormHeader__row'>
          <CustomTitle
            text={isEditing ? 'Edit AI agent' : 'Add AI agent'}
            fontSize={20}
            fontWeight='700'
            lineHeight='32px'
            color='var(--text-primary)'
            marginBottom={0}
          />

          {!readOnly && (
            <button
              type='button'
              onClick={handleTestClick}
              disabled={testLoading}
              className='agentFormTestBtn'
            >
              <StarIcon color='var(--text-primary)' />{' '}
              {testLoading ? 'Testing...' : 'Test agent'}
            </button>
          )}
        </div>
      </div>

      <SetupAgentTestModal
        open={testOpen}
        setOpen={setTestOpen}
        loading={!!testLoading}
        defaultChannel={callEnabled ? 'call' : 'email'}
        callEnabled={callEnabled}
        emailEnabled={emailEnabled}
        testResultsPolling={testResultsPolling}
        testResults={testResults}
        onSubmit={(payload) => {
          onRunTest?.(payload);
        }}
        onClose={onClearTestResults}
      />
    </>
  );
};

export default AgentHeader;
